/**
 * CE QU'ON FAIT D'UNE CONFIRMATION LUE — lot 124.
 *
 * Le lot 123 a posé la route qui lit un PDF et rend une proposition. Ce
 * fichier fait le pas suivant, et un seul : TRANSFORMER CETTE PROPOSITION EN
 * SAISIE, celle du formulaire « Nouvel ordre de transport » que l'exploitation
 * connaît déjà.
 *
 * ---------------------------------------------------------------------------
 * POURQUOI LE FORMULAIRE HABITUEL, ET PAS UN ÉCRAN NEUF
 * ---------------------------------------------------------------------------
 * Décidé avec Mohamed le 26/10/2026, après vérification : `SaisieEtape`
 * (`ot.ts:1057`) porte EXACTEMENT les champs que la lecture extrait — type,
 * site, adresse, code postal, ville, jour, heures, quantité, support, poids,
 * note.
 *
 * Un écran neuf aurait donc redessiné ce qui existe, et il aurait fallu
 * l'entretenir en double à chaque changement du modèle d'ordre. Le formulaire
 * s'ouvre pré-rempli, on corrige, on valide par le chemin éprouvé.
 *
 * ---------------------------------------------------------------------------
 * CE QUI N'EST JAMAIS PRÉ-REMPLI, ET POURQUOI
 * ---------------------------------------------------------------------------
 *   - LE CENTRE DE PROFIT. Il ne figure sur aucune confirmation, et le code
 *     le disait déjà : « rien ne la pré-remplit » (`ot.ts:885`). L'exploitation
 *     le désigne, comme pour tout ordre saisi à la main.
 *   - LE CLIENT, tant que le rapprochement n'est pas CERTAIN. C'est la règle du
 *     22/10 : Stafa ne crée jamais un client tout seul, et n'en choisit un
 *     d'autorité que si le rapprochement ne laisse aucun doute.
 *   - LE SITE, aux mêmes conditions. Un site inconnu se crée depuis l'écran,
 *     sur un geste.
 *
 * UN CHAMP LAISSÉ VIDE SE VOIT ; UN CHAMP MAL REMPLI SE RECOPIE. C'est tout le
 * principe de ce fichier, et c'est pour cela qu'il préfère rendre la main
 * plutôt que de deviner.
 */

import { etapeVide, ORDRE_VIDE, type SaisieEtape, type SaisieOrdre } from './ot.js';

// ---------------------------------------------------------------------------
// Ce que la route du lot 123 rend
// ---------------------------------------------------------------------------

/**
 * LES TYPES SONT REDÉCLARÉS ICI, et ce n'est pas un oubli.
 *
 * Le navigateur ne partage pas le code de l'API — seul `@stafa/core` voyage
 * entre les deux, et il porte du CALCUL, pas des formes de réponse HTTP. Tous
 * les écrans de Stafa font ainsi : `ordreAffretement.ts` redéclare les siennes.
 *
 * Ce qui les tient ensemble n'est donc pas le compilateur, c'est le test
 * d'intégration de la route : s'il change de forme, il tombe.
 */
export interface CandidatLu {
  readonly id: string;
  readonly nom: string;
  readonly note: number;
  readonly pourquoi: string;
}

export type VerdictLu =
  | { readonly quoi: 'retenu'; readonly fiche: CandidatLu }
  | { readonly quoi: 'a_choisir'; readonly candidats: readonly CandidatLu[] }
  | { readonly quoi: 'inconnu' };

export interface EtapeProposee {
  readonly type: 'chargement' | 'livraison';
  readonly site_nom: string;
  readonly site_adresse: string | null;
  readonly site_code_postal: string | null;
  readonly site_ville: string | null;
  readonly jour: string | null;
  readonly heure_debut: string | null;
  readonly heure_fin: string | null;
  readonly quantite: number | null;
  readonly support: string | null;
  readonly poids: number | null;
  readonly note: string | null;
  readonly site: VerdictLu;
}

export interface TransportPropose {
  readonly client_nom: string | null;
  readonly client_siret: string | null;
  readonly reference: string | null;
  readonly marchandise: string | null;
  readonly prix: number | null;
  readonly prix_gasoil_inclus: boolean | null;
  /** Lot 145 : écrite sur le document, sinon `null` — jamais estimée. */
  readonly distance_km?: number | null;
  /** Lot 145 : un code du référentiel `vehicules`, sinon `null`. */
  readonly gabarit_code?: string | null;
  readonly client: VerdictLu;
  readonly etapes: readonly EtapeProposee[];
}

/** La réponse entière de `POST /confirmations/lire`. */
export type LectureFaite =
  | { readonly lu: false; readonly message: string; readonly statut?: number }
  | {
      readonly lu: true;
      readonly transports: readonly TransportPropose[];
      readonly remarques: readonly string[];
      readonly modele: string;
    };

// ---------------------------------------------------------------------------
// De la proposition à la saisie
// ---------------------------------------------------------------------------

/**
 * UN NOMBRE DEVIENT LE TEXTE QUE LE FORMULAIRE ATTEND.
 *
 * `SaisieOrdre` et `SaisieEtape` ne portent que des chaînes : ce sont les
 * valeurs d'un formulaire, pas des grandeurs. `null` devient la chaîne vide,
 * jamais « 0 » — un champ vide se remplit, un zéro se croit renseigné.
 */
function enTexte(valeur: number | null): string {
  return valeur === null ? '' : String(valeur);
}

function ouVide(valeur: string | null): string {
  return valeur ?? '';
}

/**
 * L'IDENTIFIANT RETENU PAR LE RAPPROCHEMENT, ET SEULEMENT S'IL EST CERTAIN.
 *
 * « à choisir » rend la chaîne vide, même quand un candidat est excellent.
 * C'est délibéré, et c'est le cœur de la sûreté du lot : trois candidats à 90
 * laissent le champ vide, et l'écran montre les trois. Prendre le premier
 * reviendrait à décider par ordre alphabétique.
 */
export function identifiantCertain(verdict: VerdictLu): string {
  return verdict.quoi === 'retenu' ? verdict.fiche.id : '';
}

export interface SaisieProposee {
  readonly ordre: SaisieOrdre;
  readonly etapes: readonly SaisieEtape[];
}

/**
 * LA PROPOSITION DEVIENT UNE SAISIE.
 *
 * LE JOUR DE REPLI EST CELUI D'AUJOURD'HUI, passé en argument plutôt que lu
 * dans l'horloge : une fonction qui lit l'heure ne se teste pas deux fois
 * pareil. `etapeVide` l'exige de toute façon.
 *
 * ET UNE ÉTAPE SANS DATE GARDE LE CHAMP VIDE, elle ne prend pas le jour de
 * repli : le repli ne sert qu'à construire l'objet. Écrire la date du jour
 * dans une étape dont le document ne dit rien serait exactement l'erreur que
 * tout ce chantier cherche à éviter.
 */
export function saisieDepuis(
  transport: TransportPropose,
  jourDeRepli: string,
): SaisieProposee {
  const ordre: SaisieOrdre = {
    ...ORDRE_VIDE,
    client_id: identifiantCertain(transport.client),
    reference_client: ouVide(transport.reference),
    marchandise: ouVide(transport.marchandise),
    prix_vente: enTexte(transport.prix),
    // LOT 145 — les champs de la nouvelle saisie. L'activité n'est pas lue :
    // « il peut la laisser, je la remplis moi-même » (Mohamed, 02/11).
    gabarit_code: ouVide(transport.gabarit_code ?? null),
    distance_km: enTexte(transport.distance_km ?? null),
  };

  const etapes = transport.etapes.map((e) => ({
    ...etapeVide(e.type, jourDeRepli),
    site_id: identifiantCertain(e.site),
    site_nom: e.site_nom,
    site_adresse: ouVide(e.site_adresse),
    site_code_postal: ouVide(e.site_code_postal),
    site_ville: ouVide(e.site_ville),
    jour: ouVide(e.jour),
    heure_debut: ouVide(e.heure_debut),
    heure_fin: ouVide(e.heure_fin),
    quantite: enTexte(e.quantite),
    support: ouVide(e.support),
    poids: enTexte(e.poids),
    note: ouVide(e.note),
  }));

  return { ordre, etapes };
}

// ---------------------------------------------------------------------------
// Ce qu'il reste à faire, dit en clair
// ---------------------------------------------------------------------------

/** Un point que l'écran doit montrer avant qu'on enregistre. */
export interface PointAVerifier {
  /** `bloquant` empêche d'enregistrer ; `attention` ne fait que prévenir. */
  readonly gravite: 'bloquant' | 'attention';
  readonly texte: string;
}

/**
 * CE QUI MANQUE POUR POUVOIR ENREGISTRER, ET CE QUI MÉRITE UN COUP D'ŒIL.
 *
 * ---------------------------------------------------------------------------
 * POURQUOI CETTE LISTE EST CALCULÉE ICI ET NON DESSINÉE DANS L'ÉCRAN
 * ---------------------------------------------------------------------------
 * Parce qu'elle se teste. Un bandeau d'avertissement écrit à même le JSX ne
 * s'éprouve qu'à l'œil, et la leçon du 14/09 est écrite dans les défauts en
 * attente : « un message écrit n'est pas un message affiché ». Ici le contenu
 * est une valeur, et un test peut tenir chaque phrase.
 *
 * ---------------------------------------------------------------------------
 * CE QUI EST BLOQUANT EST CE QUE LA BASE REFUSERAIT
 * ---------------------------------------------------------------------------
 * `ordres_transport.client_id` et `etapes.site_id` sont `not null`, et
 * `etapes.jour` aussi. Un ordre sans eux ne s'enregistre pas : autant le dire
 * tout de suite plutôt que de laisser l'API répondre par un refus que
 * personne ne relie à la confirmation lue.
 *
 * Le centre de profit est obligatoire lui aussi, mais il n'est JAMAIS sur le
 * document : il n'est donc pas listé ici comme un manque de la lecture — ce
 * serait reprocher au PDF de ne pas porter une information qui n'y est pas.
 */
export function pointsAVerifier(transport: TransportPropose): readonly PointAVerifier[] {
  const points: PointAVerifier[] = [];

  if (transport.client.quoi === 'retenu') {
    // RIEN À DIRE, et c'est voulu : le rapprochement certain est le cas
    // ordinaire, et le signaler noierait les vrais points d'attention.
  } else if (transport.client.quoi === 'a_choisir') {
    points.push({
      gravite: 'bloquant',
      texte: `Client « ${transport.client_nom ?? '?'} » : ${
        transport.client.candidats.length === 1 ? 'une fiche possible' : 'plusieurs fiches possibles'
      }, à confirmer.`,
    });
  } else {
    points.push({
      gravite: 'bloquant',
      texte:
        transport.client_nom === null
          ? "Aucun client n'a été lu sur ce document : à désigner."
          : `Client « ${transport.client_nom} » inconnu de l'annuaire. Stafa ne crée jamais un client tout seul : à désigner ou à créer dans l'annuaire.`,
    });
  }

  if (transport.reference === null) {
    points.push({
      gravite: 'bloquant',
      texte:
        "Aucune référence à rappeler sur la facture n'a été lue. Sans elle, la facture risque le retour.",
    });
  }

  if (transport.prix === null) {
    points.push({ gravite: 'attention', texte: "Aucun prix n'a été lu." });
  } else if (transport.prix_gasoil_inclus === true) {
    points.push({
      gravite: 'attention',
      texte: `Prix de ${transport.prix} € GASOIL INCLUS : l'indexation de Stafa s'ajouterait par-dessus.`,
    });
  }

  for (const etape of transport.etapes) {
    const ou = `${etape.type === 'chargement' ? 'Chargement' : 'Livraison'} ${etape.site_nom}`;
    if (etape.site.quoi === 'inconnu') {
      points.push({
        gravite: 'bloquant',
        texte: `${ou} : site inconnu de l'annuaire, à créer ou à désigner.`,
      });
    } else if (etape.site.quoi === 'a_choisir') {
      points.push({ gravite: 'bloquant', texte: `${ou} : site à confirmer.` });
    }
    if (etape.jour === null) {
      points.push({ gravite: 'bloquant', texte: `${ou} : aucune date lue.` });
    }
    if (etape.heure_debut === null && etape.heure_fin === null) {
      points.push({ gravite: 'attention', texte: `${ou} : aucun horaire lu.` });
    }
  }

  return points;
}

/** Vrai quand rien n'empêche d'enregistrer. */
export function enregistrable(points: readonly PointAVerifier[]): boolean {
  return !points.some((p) => p.gravite === 'bloquant');
}

/**
 * CE QU'IL FAUT POUR CRÉER LE SITE MANQUANT D'UNE ÉTAPE.
 *
 * Rendu `null` quand le site est déjà connu : l'écran n'affiche alors aucun
 * bouton de création, et on ne peut pas créer par mégarde un doublon d'une
 * fiche qu'on vient de retrouver.
 */
export function siteACreer(etape: EtapeProposee): {
  readonly nom: string;
  readonly adresse: string;
  readonly code_postal: string;
  readonly ville: string;
} | null {
  if (etape.site.quoi !== 'inconnu') return null;
  return {
    nom: etape.site_nom,
    adresse: ouVide(etape.site_adresse),
    code_postal: ouVide(etape.site_code_postal),
    ville: ouVide(etape.site_ville),
  };
}

// ---------------------------------------------------------------------------
// Le document, en route vers l'API
// ---------------------------------------------------------------------------

/**
 * LES OCTETS D'UN FICHIER DEVIENNENT DU BASE64.
 *
 * ---------------------------------------------------------------------------
 * POURQUOI PAS `btoa(String.fromCharCode(...octets))`
 * ---------------------------------------------------------------------------
 * C'est la formule qu'on trouve partout, et elle CASSE sur un vrai document.
 * `...` étale chaque octet en argument de fonction, et le nombre d'arguments
 * d'un appel est borné — autour de 65 000 selon les navigateurs. Une
 * confirmation de 250 Ko fait 250 000 octets : l'appel échoue avec un
 * débordement de pile, pas avec une erreur qui nomme la cause.
 *
 * Le plus traître est que ça MARCHE à l'essai, sur un petit fichier, et que ça
 * tombe en production sur le premier scan un peu lourd.
 *
 * On avance donc par tranches de 8 Ko, loin de toute borne.
 *
 * ---------------------------------------------------------------------------
 * CE QUE CETTE FONCTION NE FAIT PAS
 * ---------------------------------------------------------------------------
 * Elle ne lit pas le fichier : `FileReader` et `File` appartiennent au
 * navigateur et ne se testent pas sans lui. Elle reçoit des octets, elle rend
 * du texte, et c'est tout — donc elle se vérifie ici, sans navigateur.
 */
export function enBase64(octets: ArrayBuffer): string {
  const vue = new Uint8Array(octets);
  const TRANCHE = 8192;
  let texte = '';
  for (let debut = 0; debut < vue.length; debut += TRANCHE) {
    const bout = vue.subarray(debut, Math.min(debut + TRANCHE, vue.length));
    texte += String.fromCharCode(...bout);
  }
  return btoa(texte);
}

/**
 * CE QUE STAFA ACCEPTE DE LIRE, et ce qu'il refuse tout de suite.
 *
 * LE REFUS EST IMMÉDIAT ET LOCAL, avant tout envoi : une erreur de dépôt n'a
 * pas à coûter trente secondes d'attente et un appel au modèle. La route pose
 * les mêmes bornes de son côté — on ne fait pas confiance à l'écran pour
 * garder la porte.
 *
 * LA LIMITE DE 7 Mo est celle du document AVANT encodage : le base64 l'enfle
 * d'un tiers, et la route s'arrête à 10 Mo d'encodé. Une confirmation pèse
 * quelques centaines de kilooctets ; au-delà de sept mégaoctets, ce n'est pas
 * une confirmation, c'est une erreur de dépôt.
 */
export const MAX_OCTETS = 7 * 1024 * 1024;

export function refusDuDocument(fichier: {
  readonly nom: string;
  readonly type: string;
  readonly taille: number;
}): string | null {
  const estPdf =
    fichier.type === 'application/pdf' || fichier.nom.toLowerCase().endsWith('.pdf');
  if (!estPdf) {
    return `« ${fichier.nom} » n'est pas un PDF. Seules les confirmations en PDF sont lues pour l'instant.`;
  }
  if (fichier.taille === 0) {
    return `« ${fichier.nom} » est vide.`;
  }
  if (fichier.taille > MAX_OCTETS) {
    const mo = Math.round((fichier.taille / 1024 / 1024) * 10) / 10;
    return `« ${fichier.nom} » pèse ${mo} Mo. Une confirmation en fait quelques centaines de kilooctets : vérifiez le fichier.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Où en est la lecture, pour l'écran
// ---------------------------------------------------------------------------

/**
 * L'ÉTAT DE LA LECTURE, tel que l'écran le montre.
 *
 * UNE VALEUR, PAS DU DESSIN, et c'est délibéré : la leçon du 14/09/2026,
 * consignée dans les défauts en attente, dit qu'« un message écrit n'est pas
 * un message affiché ». Un bandeau rédigé à même le JSX ne s'éprouve qu'à
 * l'œil ; celui-ci se teste.
 */
export type EtatLecture =
  | { readonly quoi: 'repos' }
  /** Le document part, on attend le modèle. Quelques secondes. */
  | { readonly quoi: 'en_cours'; readonly nom: string }
  /** Rien n'a été lu, et on dit pourquoi. Ce n'est pas forcément une panne. */
  | { readonly quoi: 'refus'; readonly message: string }
  | {
      readonly quoi: 'faite';
      readonly nom: string;
      readonly points: readonly PointAVerifier[];
      readonly remarques: readonly string[];
      /** Combien d'AUTRES transports ce document portait, non repris ici. */
      readonly autres: number;
    };

/**
 * CE QUE L'ÉCRAN DOIT MONTRER APRÈS UNE LECTURE, et quelle saisie ouvrir.
 *
 * ---------------------------------------------------------------------------
 * UN SEUL TRANSPORT À LA FOIS, ET LE DOCUMENT LE DIT
 * ---------------------------------------------------------------------------
 * Décidé le 23/10 : « une proposition par transport ». Le formulaire de Stafa
 * ne crée qu'un ordre à la fois — on ouvre donc le PREMIER, et on annonce
 * combien d'autres attendent.
 *
 * ON NE LES PERD PAS EN SILENCE. Un document à trois transports dont on ne
 * saisirait qu'un sans le dire est exactement la façon dont deux transports
 * disparaissent : l'écran porte « 2 autres transports sur ce document », et
 * c'est l'exploitation qui décide de relire le PDF.
 */
export function suiteDeLaLecture(
  faite: LectureFaite,
  nomFichier: string,
  jourDeRepli: string,
):
  | { readonly etat: EtatLecture; readonly saisie: SaisieProposee }
  | { readonly etat: EtatLecture; readonly saisie: null } {
  if (!faite.lu) {
    return { etat: { quoi: 'refus', message: faite.message }, saisie: null };
  }

  const premier = faite.transports[0];
  if (premier === undefined) {
    return {
      etat: {
        quoi: 'refus',
        message:
          "Aucun transport n'a été trouvé dans ce document. S'agit-il bien d'une confirmation ?",
      },
      saisie: null,
    };
  }

  return {
    etat: {
      quoi: 'faite',
      nom: nomFichier,
      points: pointsAVerifier(premier),
      remarques: faite.remarques,
      autres: faite.transports.length - 1,
    },
    saisie: saisieDepuis(premier, jourDeRepli),
  };
}
